import type { WorkspaceSummary } from './workspaces';

/**
 * The last good Spaces list, kept in this browser per signed-in account, so the sidebar paints its Spaces on the
 * first frame after a reload instead of ~1.7 s later (page code, then a ListWorkspaces read). The network read
 * still runs at once and replaces it; this only decides what shows while it is in flight.
 *
 * **Whose list.** The entry carries the account it was saved for, and a read answers only when that is the
 * account of THIS page load. The account is not something the browser can see — the Wix session cookie is
 * HttpOnly — so the server reads it off the document request (`accountTagFromCookieHeader`, in `_app`'s
 * getInitialProps) and hands the page a hash of it. No tag (no session, a dev origin, a client-side navigation
 * before one was adopted) means no seed and no write: never another account's list.
 *
 * **Sign-out.** `wixLogout` clears it, and a new sign-in is a new session with its own tag anyway.
 *
 * Storage is optional: every access is wrapped, and a refused, full or corrupt store reads as a miss.
 */

const STORAGE_KEY = 'symphony.workspaces.v1';
const MAX_AGE_MS = 14 * 24 * 60 * 60_000;
const MAX_BYTES = 300_000;
const MAX_ROWS = 500;

interface Snapshot {
  schema: 1;
  owner: string;
  savedAt: number;
  workspaces: WorkspaceSummary[];
}

let owner: string | null = null;

/**
 * The account of this page load (a hash, never the session). Ignored when empty, and on the server, where this module
 * is shared by every request and there is no storage to read anyway.
 */
export function adoptWorkspacesCacheOwner(tag: string | null | undefined): void {
  if (typeof window === 'undefined') return;
  if (typeof tag === 'string' && tag) owner = tag;
}

/** The adopted account tag; for tests. */
export function workspacesCacheOwner(): string | null {
  return owner;
}

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

function isRow(value: unknown): value is WorkspaceSummary {
  if (!value || typeof value !== 'object') return false;
  const row = value as Record<string, unknown>;
  return typeof row.id === 'string' && row.id !== '' && typeof row.slug === 'string' && row.slug !== ''
    && typeof row.name === 'string' && typeof row.projects === 'number'
    && (row.icon === undefined || typeof row.icon === 'string')
    && (row.description === undefined || typeof row.description === 'string')
    && (row.website === undefined || (typeof row.website === 'object' && row.website !== null))
    && (row.presence === undefined || (typeof row.presence === 'object' && row.presence !== null));
}

/** This account's last good list, or null: none saved, another account's, too old, or not the expected shape. */
export function readCachedWorkspaces(): WorkspaceSummary[] | null {
  if (!owner) return null;
  try {
    const raw = storage()?.getItem(STORAGE_KEY);
    if (!raw || raw.length > MAX_BYTES) return null;
    const value = JSON.parse(raw) as Partial<Snapshot> | null;
    if (!value || value.schema !== 1 || value.owner !== owner) return null;
    const age = typeof value.savedAt === 'number' ? Date.now() - value.savedAt : Infinity;
    if (!(age >= 0 && age <= MAX_AGE_MS)) return null;
    const rows = value.workspaces;
    if (!Array.isArray(rows) || rows.length > MAX_ROWS || !rows.every(isRow)) return null;
    return rows;
  } catch {
    return null;
  }
}

/** Saves a list the server just answered, for this account. One entry per browser: the last account wins. */
export function writeCachedWorkspaces(workspaces: WorkspaceSummary[]): void {
  if (!owner || !Array.isArray(workspaces) || workspaces.length > MAX_ROWS) return;
  try {
    const raw = JSON.stringify({ schema: 1, owner, savedAt: Date.now(), workspaces } satisfies Snapshot);
    if (raw.length > MAX_BYTES) return;
    storage()?.setItem(STORAGE_KEY, raw);
  } catch {
    /* private mode / quota: the network read still serves this load */
  }
}

/** Forgets the saved list (sign-out, a 401). */
export function clearCachedWorkspaces(): void {
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to forget where nothing could be written */
  }
}

/**
 * The account tag of a document request, from its `Cookie:` header: a SHA-256 of the Wix session's user id when the
 * session token's payload names one (stable across the session's renewals), else of the whole session cookie. Null
 * when there is no session or no Web Crypto. A cache key, never an authorization: the server never trusts it.
 */
export async function accountTagFromCookieHeader(cookieHeader: string | undefined | null): Promise<string | null> {
  try {
    const match = /(?:^|;\s*)wixSession2=([^;]*)/.exec(cookieHeader ?? '');
    let session = match ? match[1] : '';
    try {
      session = decodeURIComponent(session);
    } catch {
      /* keep the raw value */
    }
    if (!session) return null;
    const subtle = globalThis.crypto?.subtle;
    if (!subtle) return null;
    const basis = sessionUser(session) ?? `session:${session}`;
    const digest = await subtle.digest('SHA-256', new TextEncoder().encode(basis));
    return Array.from(new Uint8Array(digest).slice(0, 16), (byte) => byte.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}

/** The user id a Wix session token's payload carries (`data.userGuid`), or null when it cannot be read. */
function sessionUser(session: string): string | null {
  try {
    const parts = session.split('.');
    for (const part of parts) {
      if (part.length < 16) continue;
      const json = base64UrlDecode(part);
      if (!json.startsWith('{')) continue;
      const payload = JSON.parse(json) as { data?: unknown };
      const data = typeof payload.data === 'string' ? (JSON.parse(payload.data) as unknown) : payload.data;
      const user = data && typeof data === 'object' ? (data as Record<string, unknown>).userGuid : undefined;
      if (typeof user === 'string' && user) return `user:${user.toLowerCase()}`;
    }
  } catch {
    /* not the shape we know: fall back to the whole cookie */
  }
  return null;
}

function base64UrlDecode(segment: string): string {
  const b64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
  return typeof atob === 'function' ? atob(b64 + pad) : Buffer.from(b64 + pad, 'base64').toString('binary');
}
